"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  FileText,
  Users,
  FolderKanban,
  FileStack,
  Settings,
  ChevronLeft,
  LayoutDashboard,
  Shield,
  ShieldCheck,
  MessageSquare,
  BookOpen,
  Gauge,
  Cable,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { BRANDING } from "@/config/branding";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useUIStore } from "@/stores/ui-store";
import { useAuthStore } from "@/stores/auth-store";
import { SpaceSidebar } from "./SpaceSidebar";
import { UsageWidget } from "./UsageWidget";

interface NavItem {
  /** Key en el namespace `sidebar` del catálogo. */
  key: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
  /** Feature flag opt-in: si está presente, el item solo se muestra si
   *  el user tiene el flag correspondiente en true. Ver Sidebar.tsx. */
  requiresFeature?: "usesConformity";
}

const mainNavItems: NavItem[] = [
  { key: "dashboard", href: "/", icon: LayoutDashboard },
  { key: "spaces", href: "/spaces", icon: FileText },
  { key: "chat", href: "/chat", icon: MessageSquare },
  { key: "categories", href: "/categories", icon: FolderKanban },
  { key: "documents", href: "/documents", icon: FileStack },
  { key: "manual", href: "/manual", icon: BookOpen },
  // Addon opt-in: Conformidad. AUDITOR se añade por si en el futuro quiere
  // acceder desde su propio dashboard sin pasar por admin.
  {
    key: "conformity",
    href: "/conformity",
    icon: ShieldCheck,
    roles: ["ROLE_USER", "ROLE_AUDITOR", "ROLE_ADMIN", "ROLE_SUPERADMIN"],
    requiresFeature: "usesConformity",
  },
];

const adminNavItems: NavItem[] = [
  { key: "users", href: "/users", icon: Users, roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"] },
  { key: "administration", href: "/admin", icon: Shield, roles: ["ROLE_ADMIN", "ROLE_SUPERADMIN"] },
  { key: "sogohub_connector", href: "/admin/connectors/sogohub", icon: Cable, roles: ["ROLE_SUPERADMIN"] },
  { key: "quotas", href: "/quota-requests", icon: Gauge, roles: ["ROLE_SUPERADMIN"] },
];

interface NavLinkProps {
  item: NavItem;
  pathname: string;
  sidebarCollapsed: boolean;
  /** Etiqueta ya traducida — el hook useTranslations se llama en el padre. */
  label: string;
}

const NavLink = React.memo(({ item, pathname, sidebarCollapsed, label }: NavLinkProps) => {
  // For root path "/", only exact match. For others, also match children routes.
  const isActive = item.href === "/"
    ? pathname === "/"
    : pathname === item.href || pathname.startsWith(`${item.href}/`);
  const Icon = item.icon;

  const linkEl = (
    <Link
      href={item.href}
      className={cn(
        "sidebar-menu__entry__link",
        isActive && "active",
        sidebarCollapsed && "justify-center px-2"
      )}
      aria-label={label}
      tabIndex={0}
    >
      <Icon className="sidebar-menu__entry__icon" />
      {!sidebarCollapsed && <span>{label}</span>}
    </Link>
  );

  return (
    <div className="sidebar-menu__entry">
      {sidebarCollapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      ) : (
        linkEl
      )}
    </div>
  );
});

NavLink.displayName = "NavLink";

export const Sidebar = () => {
  const pathname = usePathname();
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
  const { user } = useAuthStore();
  const t = useTranslations("sidebar");

  // Match /spaces/<id>/... — render the space-contextual sidebar.
  const spaceDetailMatch = pathname.match(/^\/spaces\/(\d+)(?:\/.*)?$/);
  if (spaceDetailMatch) {
    const spaceId = Number(spaceDetailMatch[1]);
    if (!isNaN(spaceId)) {
      return <SpaceSidebar spaceId={spaceId} />;
    }
  }

  const userRoles = user?.roles || [];

  const hasAccess = (roles?: string[]) => {
    if (!roles) return true;
    return roles.some((role) => userRoles.includes(role as never));
  };

  const hasFeature = (flag?: NavItem["requiresFeature"]) => {
    if (!flag) return true;
    // SUPERADMIN ve todos los addons aunque su flag esté off (paridad
    // con el bypass del backend en require_conformity_feature).
    if (userRoles.includes("ROLE_SUPERADMIN" as never)) return true;
    return !!user?.[flag];
  };

  const canShow = (item: NavItem) => hasAccess(item.roles) && hasFeature(item.requiresFeature);

  return (
    <aside
      className={cn(
        "layout__sidebar sidebar",
        sidebarCollapsed && "layout__sidebar--slim sidebar--slim"
      )}
    >
      {/* Header */}
      <div className="sidebar__section flex items-center justify-between border-b border-[var(--gray-200)]">
        <Link href="/" className="sidebar__brand flex items-center gap-2">
          <Image
            src={BRANDING.LOGO_PATH}
            alt={BRANDING.LOGO_ALT}
            width={BRANDING.LOGO_SIDEBAR_WIDTH}
            height={BRANDING.LOGO_SIDEBAR_HEIGHT}
            priority
            className="object-contain"
          />
          {!sidebarCollapsed && (
            <span className="text-lg font-bold tracking-tight" style={{ color: "var(--gray-900)" }}>
              {BRANDING.APP_NAME}
            </span>
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebarCollapsed}
          className={cn("h-8 w-8", sidebarCollapsed && "mx-auto")}
          aria-label={sidebarCollapsed ? t("expand_menu") : t("collapse_menu")}
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform duration-300",
              sidebarCollapsed && "rotate-180"
            )}
          />
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="sidebar__section--fluid">
        <nav className="sidebar-menu">
          {mainNavItems
            .filter(canShow)
            .map((item) => (
              <NavLink
                key={item.href}
                item={item}
                pathname={pathname}
                sidebarCollapsed={sidebarCollapsed}
                label={t(item.key)}
              />
            ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="sidebar__section border-t border-[var(--gray-200)]">
        {adminNavItems.some(canShow) && (
          <nav className="sidebar-menu">
            {!sidebarCollapsed && (
              <p className="mb-2 px-4 pt-2 text-xs font-semibold uppercase" style={{ color: "var(--gray-500)" }}>
                {t("administration")}
              </p>
            )}
            {adminNavItems
              .filter(canShow)
              .map((item) => (
                <NavLink
                  key={item.href}
                  item={item}
                  pathname={pathname}
                  sidebarCollapsed={sidebarCollapsed}
                  label={t(item.key)}
                />
              ))}
          </nav>
        )}
        <UsageWidget collapsed={sidebarCollapsed} />
        <div className="sidebar-menu__entry">
          {(() => {
            const settingsLabel = t("settings");
            const settingsLink = (
              <Link
                href="/settings"
                className={cn(
                  "sidebar-menu__entry__link",
                  pathname === "/settings" && "active",
                  sidebarCollapsed && "justify-center px-2"
                )}
                aria-label={settingsLabel}
                tabIndex={0}
              >
                <Settings className="sidebar-menu__entry__icon" />
                {!sidebarCollapsed && <span>{settingsLabel}</span>}
              </Link>
            );
            return sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>{settingsLink}</TooltipTrigger>
                <TooltipContent side="right">{settingsLabel}</TooltipContent>
              </Tooltip>
            ) : settingsLink;
          })()}
        </div>
      </div>
    </aside>
  );
};
